export type LegalTemplateKey = "rental_agreement" | "terms_conditions" | "deposit_policy";
export type LegalTemplateSource = "system" | "custom";

export interface LegalTemplateEntry {
  source: LegalTemplateSource;
  custom_markdown: string | null;
}

export interface LegalTemplatesConfig {
  rental_agreement: LegalTemplateEntry;
  terms_conditions: LegalTemplateEntry;
  deposit_policy: LegalTemplateEntry;
}

export interface LegalTemplateSectionDefinition {
  key: LegalTemplateKey;
  title: string;
  description: string;
}

export const LEGAL_TEMPLATE_SECTIONS: LegalTemplateSectionDefinition[] = [
  {
    key: "rental_agreement",
    title: "Rental Agreement",
    description: "Core rental contract covering the handover, permitted use, liability, and return duties.",
  },
  {
    key: "terms_conditions",
    title: "Terms & Conditions",
    description: "Business-wide rules that apply to reservations, payments, use standards, and disputes.",
  },
  {
    key: "deposit_policy",
    title: "Deposit Policy",
    description: "How security deposits are collected, reviewed, withheld when needed, and released.",
  },
];

export const SYSTEM_LEGAL_TEMPLATES: Record<LegalTemplateKey, string> = {
  rental_agreement: `# Rental Agreement

## 1. Rental Scope
- The renter receives temporary use of the booked vehicle for the rental dates, rates, and add-ons shown in the confirmed booking summary.
- The vehicle may only be used for lawful personal or business transportation that complies with local traffic, licensing, and insurance requirements.

## 2. Driver Responsibilities
- The renter confirms that every driver is legally licensed, medically fit to drive, and authorized by the rental business before operating the vehicle.
- The renter is responsible for the conduct of any approved additional driver and for all fines, tolls, penalties, towing costs, and impound fees arising during the rental period.

## 3. Vehicle Care
- The renter must keep the vehicle reasonably clean, locked when unattended, and protected from misuse, negligence, overloading, racing, off-road driving, or use on prohibited roads.
- Smoking, transporting hazardous materials, or using the vehicle for unlawful activity is prohibited unless the rental business has expressly approved the use in writing.

## 4. Damage, Loss, and Incidents
- The renter must report any accident, theft, warning light, mechanical issue, or third-party claim as soon as reasonably possible and cooperate with incident documentation.
- The renter remains responsible for damage, missing equipment, excess wear, and costs not covered by insurance, waivers, or the security deposit.

## 5. Return Conditions
- The vehicle must be returned on time, with the agreed fuel level, and with all keys, documents, devices, and accessories supplied at pickup.
- Additional charges may apply for late return, excess mileage, missing fuel, cleaning, damage, unpaid tolls, and any other approved fees disclosed in the booking terms.

## 6. Enforcement
- Failure to comply with this agreement may result in early termination of the rental, recovery of the vehicle, and collection of any outstanding balances permitted by law.
- By using the vehicle, the renter accepts these obligations together with the active Terms & Conditions and Deposit Policy selected by the rental business.`,
  terms_conditions: `# Terms & Conditions

## 1. Reservations and Eligibility
- A reservation is confirmed only after the rental business accepts the booking and records the required renter, driver, and payment details.
- The rental business may refuse or cancel a booking if eligibility, identification, payment authorization, insurance status, or vehicle availability cannot be verified.

## 2. Rates and Charges
- The renter agrees to pay the confirmed rental rate together with applicable taxes, approved extras, and any post-rental adjustments permitted under the booking terms.
- Fees may be added for excess mileage, late return, fuel differences, cleaning, damage, tolls, traffic violations, collection costs, or other charges disclosed at booking or pickup.

## 3. Permitted and Prohibited Use
- The vehicle may only be driven by approved drivers and may not be sub-rented, used for racing, driver training, towing beyond approved limits, or any unlawful purpose.
- The renter must comply with geographic restrictions, age requirements, road restrictions, and any operational limits communicated by the rental business.

## 4. Breakdowns and Accidents
- The renter must stop using the vehicle when continued operation could cause further damage or create an unsafe condition.
- The renter must promptly notify the rental business of accidents, theft, seizure, warning lights, or roadside incidents and follow the instructions provided.

## 5. Cancellation and Early Return
- Cancellation, no-show, and early return outcomes are governed by the booking terms confirmed by the rental business at the time of reservation.
- Unless otherwise required by law or stated in writing, unused rental time is not automatically refundable after the rental has started.

## 6. Privacy and Compliance
- The rental business may use renter and booking information to administer reservations, verify identity, manage risk, recover losses, and comply with legal obligations.
- These terms apply together with the active Rental Agreement and Deposit Policy, and any conflict will be interpreted in a commercially reasonable manner permitted by law.`,
  deposit_policy: `# Deposit Policy

## 1. Deposit Collection
- A security deposit may be pre-authorized or collected before vehicle release to cover approved risk exposures connected to the rental.
- The deposit amount may vary by vehicle class, rental length, payment method, driver profile, insurance status, or any elevated risk identified during booking review.

## 2. What the Deposit Covers
- The deposit may be applied to unpaid rental balances, excess mileage, missing fuel, cleaning charges, damage, missing equipment, tolls, fines passed through by law, or similar approved costs.
- The deposit is not a cap on liability, and the renter remains responsible for balances that exceed the deposit amount.

## 3. Inspection and Review
- The rental business will review the vehicle, booking file, payment status, and any incident reports before releasing or refunding the deposit.
- When additional time is reasonably required to verify damage, tolls, violations, or third-party claims, the deposit may be held until that review is completed.

## 4. Release and Withholding
- If no outstanding issues remain, the rental business will release or refund the deposit through the original payment channel or another agreed method used by the business.
- If any amount is withheld, the rental business should keep clear records of the basis for the deduction and the amount applied.

## 5. Disputes
- The renter should raise any deposit dispute promptly with supporting information so the rental business can review the file and respond.
- This policy works together with the active Rental Agreement and Terms & Conditions selected by the rental business.`,
};

export function createDefaultLegalTemplates(): LegalTemplatesConfig {
  return {
    rental_agreement: { source: "system", custom_markdown: null },
    terms_conditions: { source: "system", custom_markdown: null },
    deposit_policy: { source: "system", custom_markdown: null },
  };
}

function isSource(value: unknown): value is LegalTemplateSource {
  return value === "system" || value === "custom";
}

function normalizeEntry(value: unknown): LegalTemplateEntry {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { source: "system", custom_markdown: null };
  }

  const entry = value as { source?: unknown; custom_markdown?: unknown };
  const customMarkdown: string | null =
    typeof entry.custom_markdown === "string" ? entry.custom_markdown : null;

  return {
    source: isSource(entry.source) ? entry.source : "system",
    custom_markdown: customMarkdown,
  };
}

export function normalizeLegalTemplates(value: unknown): LegalTemplatesConfig {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return createDefaultLegalTemplates();
  }

  const record = value as Record<string, unknown>;

  return {
    rental_agreement: normalizeEntry(record.rental_agreement),
    terms_conditions: normalizeEntry(record.terms_conditions),
    deposit_policy: normalizeEntry(record.deposit_policy),
  };
}

export function getActiveLegalTemplateMarkdown(
  templates: LegalTemplatesConfig,
  key: LegalTemplateKey,
): string {
  const entry = templates[key];

  if (entry.source === "custom" && entry.custom_markdown !== null) {
    return entry.custom_markdown;
  }

  return SYSTEM_LEGAL_TEMPLATES[key];
}
